import { Injectable } from '@angular/core';
import { Issue } from '../models/issue';
import { DrawnDependency } from '../models/drawnDependency';
import * as JSPlumb from 'jsplumb';
import { UserstoryService } from './userstory.service';
import { AwallComponentEvent } from '../models/awallComponentEvent';
import { CommunicationService, Listener } from './communication.service';
import { ConfirmationDialogComponent } from '../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ComponentEvent } from '../models/enums/componentEvent';
import { IssueLink } from '../models/issueLink';
import { DependencyEditmodeService } from './dependency-editmode.service';
import { Criticality } from '../models/enums/criticality';

/**
 * Handles drawing and updating the Dependency-Arrows between issues.
 */
@Injectable({
  providedIn: 'root',
  })
export class DependencyDrawingService implements Listener<AwallComponentEvent>  {

  // Map with key = issue.id and value = Array of the issues dependencies
  public drawnDependencies: Map<number, DrawnDependency[]> = new Map();
  // This reflects the state of the visibility-button
  public showDependencies = false;
  // This is a calculated state based on 'showDependencies' AND DependencyEditService.editModeActive
  public dependenciesVisible = false;
  private issues: Map<number, Issue> = new Map();
  private jsPlumbInstance: JSPlumb.jsPlumbInstance;
  private readonly LINE_COLOR = '#e8005e';
  private readonly HOVER_COLOR = '#3f51b5';

  constructor(
    private userstoryService: UserstoryService,
    private communcationService: CommunicationService,
    protected dialog: MatDialog,
    protected translate: TranslateService) {
    this.communcationService.registerComponent(DependencyDrawingService.name, this);
  }

  public initJSPlumb(container: HTMLElement): void {
    this.jsPlumbInstance = JSPlumb.jsPlumb.getInstance();
    this.jsPlumbInstance.importDefaults({
      ConnectionsDetachable: false,
      PaintStyle: {
        strokeWidth: 3,
        stroke: this.LINE_COLOR
      },
      HoverPaintStyle: { stroke: this.HOVER_COLOR, strokeWidth: 5 },
      Endpoints: [['Dot', { radius: 5, 'enabled': false }], 'Blank'],
      EndpointStyles: [{ fill: this.LINE_COLOR }, { fill: this.LINE_COLOR }]
    });
    this.jsPlumbInstance.setContainer(container);
  }

  /**
   * Add issues to the dependency-drawing service. Issues need to be added so their dependecies
   * can be drawn or updated.
   *
   * If an issue was already added it will be replaced by this call.
   */
  public addIssues(issues: Issue[], projectKey: string, sprintStartDate: Date): void {
    issues.forEach(issue => {
      // we need to attach projectKey info to the issues as it is needed if we want to delete the issuelink later.
      // This is not an ideal solution though.
      issue['projectKey'] = projectKey;
      issue['sprintStartDate'] = sprintStartDate;
      this.issues.set(issue.id, issue);
    });
  }

  /**
   * Removes issues from the dependency-drawing service. All drawn dependencies of these issue are also removed.
   */
  public removeIssues(issues: Issue[]): void {
    issues.forEach((issue) => {
      this.issues.delete(issue.id);
    });
  }

  /**
   * Toggles drawing or removing all dependencies. Hides private implementation
   * of updateDependencyVisibility().
   */
  public setShowDependencies(doShow: boolean): void {
    this.updateDependencyVisibility(doShow);
  }

  /**
   * This will clear and re-create all dependencies (if visibility is ON) of all issues registered with dependency-drawing-service.
   * Call this after issues have been added or removed to the dependency-drawing-service.
   */
  public updateDependencies(): void {
    this.createAllDependencies();
  }

  public repaintAll(): void {
    this.jsPlumbInstance?.repaintEverything();
  }

  /**
   * Clear the dependency-drawing service of all dependencies and issues
   */
  public clearAll(): void {
    this.clearDependencies();
    this.issues.clear();
  }

  private clearDependencies(): void {
    this.getAllDependencies().forEach(dependency => {
      dependency.removeLine(this.jsPlumbInstance);
    });
    this.drawnDependencies.clear();
  }

  /**
   * Shows or hides dependency lines and/or dependency-line-delete-buttons depending on the
   * current state of dependency-drawing-service and edit-mode-service
   *
   * @param doShow if provided this visibility-state will be applied to to dependency-drawing-service
   */
  private updateDependencyVisibility(doShow?: boolean): void {
    const hasChangedToVisible = this.udpateVisibility(doShow);
    if (hasChangedToVisible) {
      this.createAllDependencies();
    }

    const dependencyHTMLElements = document.querySelectorAll('.' + DrawnDependency.CONNECTOR_CSS_CLASS);
    Array.from(dependencyHTMLElements).forEach(x => {
      if (this.dependenciesVisible) {
        x.classList.remove('hidden');
      } else {
        x.classList.add('hidden');
      }
    });
    this.updateDeleteButtonVisibilityForAllDependencies();
  }

  /**
   * Updates the state of this.dependenciesVisible.
   *
   * @param doShow if provided this visibility-state will be applied to to dependency-drawing-service
   * @returns true if the dependency-visibility has changed from 'not visibile' to 'visible'
   */
  private udpateVisibility(doShow?: boolean): boolean {
    const oldVisibilityState = this.dependenciesVisible;

    if (doShow != null) {
      this.showDependencies = doShow;
    }
    this.dependenciesVisible = DependencyEditmodeService.editModeActive || this.showDependencies;

    const hasChangedToVisible = (!oldVisibilityState && this.dependenciesVisible);
    return hasChangedToVisible;
  }


  private updateDeleteButtonVisibilityForAllDependencies() {
    this.getAllDependencies().forEach(dependency => {
      dependency.updateDeleteButtonVisibility();
    });
  }

  private getAllDependencies(): DrawnDependency[] {
    const allDependencies: DrawnDependency[] = [];
    this.drawnDependencies.forEach((dependencies) => {
      allDependencies.push(...dependencies);
    });
    return allDependencies;
  }

  private removeDependencies(issue: Issue): void {
    if (this.drawnDependencies.has(issue.id)) {
      this.drawnDependencies.get(issue.id).forEach(dependency => {
        dependency.removeLine(this.jsPlumbInstance);
      });
      this.drawnDependencies.delete(issue.id);
    }
  }

  private createAllDependencies(): void {
    // If dependencies are not visible there is no need to create them.
    // They will be created once visibility is ON.
    if (this.dependenciesVisible) {
      this.clearDependencies();
      this.issues.forEach(issue => {
        this.createDependencies(issue);
      });
      this.repaintAll();
    }
  }

  private createDependencies(issue: Issue): void {
    const newDependencies: DrawnDependency[] = [];
    issue.issueLinks.forEach(issueLink => {
      // We check here that we only create dependencies for issues we have in our issue map
      // An target-issue might have been removed from the map after deleting the issue, but the issuelink in the source
      // might still be set (we dont update issue when their target is deleted)
      const targetIssue = this.issues.get(issueLink.issueId);
      if (!targetIssue) {
        return;
      }

      const dependency: DrawnDependency = new DrawnDependency(
        issue.id,
        issueLink.issueId,
        issueLink.id,
        this.getCriticality(issue, targetIssue),
        this.jsPlumbInstance,
        this.dependenciesVisible,
        () => this.openConfirmDeleteDialog(issueLink, issue)
      );
      newDependencies.push(dependency);
    });
    this.drawnDependencies.set(issue.id, newDependencies);
  }

  private getCriticality(sourceIssue: Issue, targetIssue: Issue): Criticality {
    const sourceSprintStartDate = new Date(sourceIssue['sprintStartDate']);
    const targetSprintStartDate = new Date(targetIssue['sprintStartDate']);

    let criticality = Criticality.Critical;
    if (sourceSprintStartDate < targetSprintStartDate) {
      criticality = Criticality.Normal;
    } else if (sourceSprintStartDate > targetSprintStartDate) {
      criticality = Criticality.Blocking;
    }
    return criticality;
  }

  openConfirmDeleteDialog(issueLink: IssueLink, issue: Issue): void {
    const title = this.translate.instant('components.deleteIssueLinkDialog.deleteConfirmationTitle');
    const message = this.translate.instant('components.deleteIssueLinkDialog.deleteConfirmationText');
    const okText = this.translate.instant('components.deleteIssueLinkDialog.deleteConfirmationOkText');
    const cancelText = this.translate.instant('components.deleteIssueLinkDialog.deleteConfirmationCancelText');

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { title: title, message: message, buttonTextConfirm: okText, buttonTextCancel: cancelText }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteDependency(issueLink, issue);
      }
    });
  }

  /**
   * Delete the Dependency in the issuetracking-system and update
   * our local issue- and dependency-models accordingly
   */
  private deleteDependency(issueLink: IssueLink, issue: Issue): void {
    // Persist the deletion and remove the issuelink from our issue and our dependencies
    if (issue) {
      this.userstoryService.deleteIssueLink(issueLink.id, issue['projectKey']);
      issue.issueLinks.splice(issue.issueLinks.indexOf(issueLink), 1);
      // remove the existing outdated dependencies of this issue
      this.removeDependencies(issue);
      // create new dependencies for the changed issue
      this.createDependencies(issue);
    }
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    switch (event?.name) {
      case ComponentEvent.DEPENDENCY_EDITMODE_CHANGED:
        this.updateDependencyVisibility();
        break;
    }
  }

  public showDebugInfo() {
    console.log('Dep.Drawing registered issues (' + this.issues.size + '):');
    this.issues.forEach(issue => {
      console.log(issue.summary + ' (' + issue.id + ')');
    });

    console.log('-----');

    console.log('Dep.Drawing existing dependencies per issue (' + this.drawnDependencies.size + '): ');
    console.log(this.drawnDependencies);
    this.drawnDependencies.forEach((value, key) => {
      console.log('Dependencies of issue: ', key);
      value.forEach(entry => {
        console.log('dep-line-source: ', entry.line.source);
        console.log('dep-line-target: ', entry.line.target);
      });
    });
    console.log('-----');

  }

}
